import { action, get } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import type { Group } from "@shared/models/group"
import type { UserSummary } from "@shared/models/user"
import FindManyAltogether from "cc-frontend/helpers/find-many-altogether"
import type Dialog from "cc-frontend/services/dialog"
import type EmberStore from "cc-frontend/services/ember-store"
import type Rpc from "cc-frontend/services/rpc"
import {
  isCachedMembershipActiveTeam,
  isGroupMembershipGroup,
  isGroupMembershipIndividual,
} from "cc-frontend/utils/group"
import { use } from "ember-could-get-used-to-this"
import { includes, isNil, pull } from "lodash-es"
import * as R from "remeda"
import { tracked } from "tracked-built-ins"

export interface SelectTeamsSignature {
  Args: {
    order: number
    closeModal: () => {}
    model: {
      userSummary: UserSummary | null
      email: string
      group: Group
      teamIds: Array<string>
    }
  }
}

export default class SelectTeams extends Component<SelectTeamsSignature> {
  @service declare rpc: Rpc
  @service("ember-store") declare store: EmberStore
  @service declare dialog: Dialog

  @tracked adjustingGroupIds: Array<String> = []
  @tracked isCreatingTeam = false

  @use teams = new FindManyAltogether(() => ["group", this.teamIds])

  get teamIds() {
    return R.pipe(
      get(this.args.model.group.attributes, "memberships"),
      R.filter(isGroupMembershipGroup),
      R.map((membership) => membership.attributes.groupId)
    )
  }

  get userGroupIds() {
    if (this.args.model.userSummary === null) {
      // We know this is true from the line above, so this next line is soley for Typescript
      if (isNil(get(this.teams, "content"))) return []
      return R.pipe(
        this.teams.content || [],
        R.filter((team: Group) => {
          let membershipIds = R.pipe(
            team.attributes.memberships,
            R.map((membership) => {
              if (!isGroupMembershipIndividual(membership)) return null
              return membership.type === "group-membership-accepted"
                ? membership.attributes.userId
                : membership.attributes.email
            }),
            R.compact
          )
          return (
            includes(membershipIds, this.args.model.email) ||
            includes(membershipIds, this.args.model.userSummary?.id)
          )
        }),
        R.map((team) => team.id)
      )
    } else {
      if (get(this.args.model, "userSummary") === null) return []
      return R.pipe(
        get(get(this.args.model.userSummary, "attributes"), "cachedMemberships"),
        R.filter(isCachedMembershipActiveTeam),
        R.filter((membership) => includes(this.args.model.teamIds, get(membership, "groupId"))),
        R.map((membership) => membership.groupId)
      )
    }
  }

  @action
  enrollUnenroll(groupId: string, currentEnrollmentStatus) {
    if (includes(this.adjustingGroupIds, groupId)) return
    this.adjustingGroupIds.push(groupId)

    let rpcPromise
    if (currentEnrollmentStatus === true) {
      rpcPromise = this.rpc.group_remove_individual({
        groupId: groupId,
        email: this.args.model.email,
      })
    } else {
      rpcPromise = this.rpc.group_add_individual({
        groupId: groupId,
        email: this.args.model.email,
        makeAdmin: false,
      })
    }
    rpcPromise.then(() => {
      if (this.isDestroyed || this.isDestroying) return
      if (this.args.model.userSummary === null) return
      this.store.find("user-summary", this.args.model.userSummary.id, true).then(() => {
        if (this.isDestroyed || this.isDestroying) return
        pull(this.adjustingGroupIds, groupId)
      })
      this.store.find("group", groupId, true).then(() => {
        if (this.isDestroyed || this.isDestroying) return
        pull(this.adjustingGroupIds, groupId)
      })
    })
  }

  @action addTeam() {
    this.dialog
      .blank("dialogs/ask-for-team-title", {
        className: "bold-dialog",
      })
      .then((nameOfNewTeam: string) => {
        this.isCreatingTeam = true
        this.rpc
          .group_create_team({
            groupId: this.args.model.group.id,
            title: nameOfNewTeam,
          })
          .finally(() => {
            this.isCreatingTeam = false
          })
      })
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Group::SelectTeams": typeof SelectTeams
  }
}
